import React, { useState } from 'react';
import { Settings, Image, ChevronDown, ChevronUp, BookOpen, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ImageScaleSettings.css';

interface ImageScaleSettingsProps {
  imageScale: number;
  onImageScaleChange: (scale: number) => void;
  showUserGuide?: boolean;
  className?: string;
}

// Date of the most recent Product Searchlight data extract.
// Update this single constant when a new extract is loaded; both the User Guide
// disclaimer and any other location that references it will stay in sync.
const DATA_EXTRACT_DATE = 'March 1, 2026';

// =============================================================================
// Visual Badge component
// -----------------------------------------------------------------------------
// Mirrors the badge styles used on real product cards (see ProductCard.tsx).
// Uses inline styles (not Tailwind classes) so the badges render correctly
// inside the User Guide modal, where parent CSS (.user-guide-modal-content td)
// would otherwise override class-based text colors.
// =============================================================================

type BadgeStyle =
  | 'default'      // Teal: FR, Hi-Vis, 🏭 Industry, 🏷️ Brand, ✨ Personalizable, Enhanced Vis, etc.
  | 'unifirst'     // Blue: "Made by UniFirst"
  | 'top10'        // Amber gradient: "Top 10 ..." style badges
  | 'bestseller'   // Yellow gradient: "⭐ Bestseller"
  | 'canada'       // Red: "Canada Only"
  | 'inStock'      // Green: "X in stock"
  | 'curated'      // Purple: "Curated" related-product corner pill
  | 'ai';          // Gray: "AI" pure-semantic-search corner pill

const BADGE_STYLES: Record<BadgeStyle, React.CSSProperties> = {
  default: {
    background: '#ccfbf1',
    color: '#115e59',
  },
  unifirst: {
    background: '#dbeafe',
    color: '#1e40af',
  },
  top10: {
    background: 'linear-gradient(to right, #fef3c7, #fef08a, #fef3c7)',
    color: '#78350f',
    border: '1px solid #fbbf24',
    fontWeight: 600,
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
  bestseller: {
    background: 'linear-gradient(to right, #fef3c7, #fef08a)',
    color: '#713f12',
    border: '1px solid #fde68a',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
  canada: {
    background: '#fee2e2',
    color: '#991b1b',
  },
  inStock: {
    background: '#dcfce7',
    color: '#166534',
  },
  curated: {
    background: '#f3e8ff',
    color: '#7e22ce',
    border: '1px solid #e9d5ff',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
  ai: {
    background: '#e3e5e8',
    color: '#53575d',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
};

const BADGE_BASE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  borderRadius: '9999px',
  fontWeight: 500,
  fontSize: '0.75rem',
  padding: '0.125rem 0.5rem',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
};

const Badge: React.FC<{ label: React.ReactNode; styleKind?: BadgeStyle; icon?: React.ReactNode }> = ({
  label,
  styleKind = 'default',
  icon,
}) => (
  <span style={{ ...BADGE_BASE_STYLE, ...BADGE_STYLES[styleKind] }}>
    {icon}
    {label}
  </span>
);

// Tiny inline-SVG icons used by the corner pills, matching the ones on the real card.
const CheckIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const SearchIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-6-6" />
  </svg>
);

// -----------------------------------------------------------------------------
// Badge reference data
// -----------------------------------------------------------------------------
// Each row shows one or more example badges plus a short description.
// Example labels intentionally mirror what users actually see on product cards.
// -----------------------------------------------------------------------------

type BadgeRow = {
  examples: Array<{ label: React.ReactNode; styleKind?: BadgeStyle; icon?: React.ReactNode }>;
  description: React.ReactNode;
};

const SAFETY_BADGES: BadgeRow[] = [
  {
    examples: [{ label: 'FR CAT 2' }, { label: 'FR CAT 4' }],
    description: <>Flame-resistant garment rated per NFPA 70E. <strong>CAT 1</strong> (≈4 cal/cm²) → <strong>CAT 4</strong> (≈40 cal/cm²) arc rating.</>,
  },
  {
    examples: [{ label: 'FR 8 cal' }],
    description: 'Flame-resistant garment with explicit ATPV value in calories/cm².',
  },
  {
    examples: [{ label: 'FR (ATPV 12)' }],
    description: 'Arc Thermal Performance Value, alternate display when only an ATPV number is known.',
  },
  {
    examples: [{ label: 'FR' }, { label: 'Flame Resistant' }],
    description: 'Generic flame-resistant garment; specific CAT or ATPV not recorded.',
  },
  {
    examples: [{ label: 'Hi-Vis Class 2' }, { label: 'Hi-Vis Class 3' }],
    description: <>ANSI/ISEA 107 high-visibility class. <strong>Class 1</strong> = off-road / warehouse; <strong>Class 2</strong> = roadside (≤25 mph traffic); <strong>Class 3</strong> = highway (≥50 mph traffic).</>,
  },
  {
    examples: [{ label: 'Hi-Vis Yellow' }],
    description: 'High-visibility garment whose class string could not be parsed.',
  },
  {
    examples: [{ label: 'Enhanced Vis' }],
    description: <>Increased visibility but does <strong>not</strong> meet a full ANSI Hi-Vis Class spec.</>,
  },
];

const BRAND_BADGES: BadgeRow[] = [
  {
    examples: [{ label: 'Made by UniFirst', styleKind: 'unifirst' }],
    description: 'Manufactured in-house by UniFirst.',
  },
  {
    examples: [{ label: '🏷️ Carhartt' }, { label: '🏷️ MOCEAN' }],
    description: 'Externally-sourced brand product (e.g., Carhartt, MOCEAN, Nike). The brand name appears after the tag emoji.',
  },
];

const INDUSTRY_BADGES: BadgeRow[] = [
  {
    examples: [{ label: '🏭 Childcare Services' }, { label: '🏭 Automotive' }],
    description: 'Product is tagged for that industry. Surfaces whenever the system detects industry context in your query — either from an explicit industry mention, from an inferred industry, or because the product is registered as relevant to that industry in the catalog.',
  },
  {
    examples: [{ label: '🏭 Industry Match' }],
    description: 'Industry-boosted result where the specific industry label was not available — generic fallback for the same concept above.',
  },
  {
    examples: [{ label: 'Childcare Services Top 10', styleKind: 'top10' }],
    description: "Among an industry's top-10 curated products. Highlighted with a gold gradient — visually distinct from the regular industry badge.",
  },
  {
    examples: [{ label: '⭐ Bestseller', styleKind: 'bestseller' }],
    description: "Among the category's most popular products by historical demand.",
  },
];

const AVAILABILITY_BADGES: BadgeRow[] = [
  {
    examples: [{ label: '✨ Personalizable' }],
    description: 'Supports embroidery, screen printing, or name embroidery.',
  },
  {
    examples: [{ label: 'Canada Only', styleKind: 'canada' }],
    description: 'Available exclusively in the Canadian market.',
  },
];

const SEARCH_MODE_BADGES: BadgeRow[] = [
  {
    examples: [{ label: 'AI', styleKind: 'ai', icon: <SearchIcon /> }],
    description: (
      <>Pure Semantic Search corner pill on a <strong>gray-tinted card</strong>. Surfaced as an alternative when traditional filters might miss it (AI meaning-based search rather than exact filter match).</>
    ),
  },
  {
    examples: [{ label: 'Curated', styleKind: 'curated', icon: <CheckIcon /> }],
    description: 'Catalog-related product surfaced because it pairs well with one of your top results (purple corner pill).',
  },
];

const FACET_TYPES: Array<{ type: string; examples: string; what: string }> = [
  { type: 'Key Feature (metric)', examples: 'CAT rating, fabric weight (oz), Hi-Vis Class', what: 'Numeric/measurable attributes, ordered by metric value.' },
  { type: 'Categorical', examples: 'Brand, Color, Fabric, Gender, Fit', what: 'Discrete labeled values.' },
  { type: 'Pattern', examples: 'Pockets, Sleeve type, Closure', what: 'Design or style patterns shared across products.' },
];

const DATA_FIELDS: Array<{ field: string; description: React.ReactNode }> = [
  { field: 'Style Code', description: <>Unique UniFirst style identifier (e.g., <code>04MM</code>, <code>123C</code>). Click in a chat response to isolate the product.</> },
  { field: 'Vendor SKU / Partial SKU', description: <>Specific configuration SKU encoding color + size (e.g., <code>04MM01BK2XL</code>).</> },
  { field: 'Description', description: 'Full product description / long name.' },
  { field: 'Product Sub-Type Category', description: 'Level-3 category (e.g., "Work Shirts").' },
  { field: 'Sub-Class Description', description: 'Level-2 category (e.g., "Shirts").' },
  { field: 'Class Description', description: 'Level-1 category (e.g., "Tops").' },
  { field: 'Fabric', description: 'Material composition (e.g., "65/35 Poly-Cotton, 6 oz").' },
  { field: 'Sizes', description: 'Available size range / list.' },
  { field: 'Colors', description: 'Available color codes and names.' },
  { field: 'Tags / Attributes', description: <>Searchable descriptive tags (e.g., <code>reinforced-knees</code>, <code>industrial-launderable</code>).</> },
  { field: 'Rental Price', description: 'Direct rental cost per garment.' },
  { field: 'Direct Sale Price', description: 'Outright purchase price.' },
  { field: 'Spec App', description: 'Industry / use-case associations from the spec application catalog.' },
  { field: 'cat_rating', description: 'Raw flame-resistant CAT rating value used in filters.' },
  { field: 'hi_vis', description: 'Raw high-visibility class value used in filters.' },
];

// =============================================================================
// Markdown sections of the User Guide (prose only — tables of visual badges
// are rendered separately by <BadgeReferenceSection /> below).
// =============================================================================

const USER_GUIDE_INTRO_MD = `## Product Searchlight — User Guide

Product Searchlight searches the UniFirst catalog to find exactly what you need — uniforms, safety gear, workwear, and specialty items across thousands of products.

---

### Visual Features

**Product Cards**

Products are displayed as **visual flip cards** in a grid layout:
- **Front:** Product image, name, style code, price, and badges (FR, UniFirst, Hi-Vis, etc.)
- **Back:** Full product details including fabric, sizes, colors, categories, and certifications
- **Click anywhere** on a card to flip it

**Collections View Toggle** (top right)

Toggle **Collections View** to group products with similar names or overlapping descriptions into a "Series" or "Collection". This makes large result sets more digestible. Collections view doesn't apply to all products or queries — use it when you want to see product families grouped together.

**Filter & Facet Bar**

Use the filter bar above the product grid to narrow results by category, brand, color, or other attributes. Facets update dynamically based on your current results.

**Display Tip:** Depending on your screen size, you may want to zoom out slightly (Ctrl/Cmd + minus) for a better visual experience with the product grid.

**Click-to-Select Style Codes**

When a style code appears in the response (like **04MM** or **123C**):
- **Click the style code** to select and isolate that product in the display
- You can click multiple style codes to build a custom selection
- Selected products will be **highlighted** for easy comparison

**Pure Semantic Search Results**

Some product cards may appear with a subtle **gray background** and an **"AI" badge** in the corner. These are **Pure Semantic Search** results — products discovered through AI-powered meaning analysis rather than exact filter matches. Hover over these cards to see the tooltip. These suggestions often surface relevant alternatives that traditional keyword filters might miss.

---

### 🏷️ Tags & Data Fields Reference

A guide to every badge, tag, and data field you may see on a product card or in the facet bar. **The badges below are rendered exactly as they appear on real product cards.**`;

const USER_GUIDE_OUTRO_MD = `---

### ⚙️ Settings (Top Right)

| Setting | Description |
|---------|-------------|
| **Image Size** | Adjust product card image size from Small to Full. Default is 25%. Use larger images to see product details, or smaller images to fit more products on screen. |

---

### Capabilities

| Capability | Description |
|-----------|-------------|
| **Multi-Filter Search** | Combine category, color, fabric, brand, gender, size, safety in one query |
| **Multi-Category Search** | Request pants, shirts, and jackets together |
| **Product Comparison** | Compare specific style codes or brands side-by-side |
| **Related Products** | Find similar items: "show me products like 04MM" |
| **Industry Top 10** | Get top uniforms for any industry: "show me uniforms for food service" |
| **Category Overview** | Get a summary: "give me an overview of our coverall offerings" |
| **Invoice Analysis** *(experimental)* | Paste competitor invoice lines to find UniFirst alternatives |
| **Industry Detection** | Automatically detects industry context and boosts relevant products |
| **Safety Understanding** | Parses FR ratings (CAT 1-4, ATPV) and hi-vis classes (ANSI 1-3) |
| **Custom Result Counts** | Default is 30 products. Ask for more ("show me 50 polos") or fewer ("5 hi-vis jackets") |
| **Smart Relaxation** | If exact filters return nothing, intelligently broadens search |

---

### Tips for Best Results

- **Be Specific:** "long sleeve navy polo shirts for men" works better than "shirts"
- **Mention Your Industry:** "chef coats for food service" gets industry-boosted results
- **Use Natural Language:** Ask questions like you would a colleague
- **Compare Products:** "compare 123C and 11CP" for side-by-side details
- **Find Similar:** "show me products like 04MM" to discover related options
- **Request Counts:** "show me all FR jeans" or "5 hi-vis jackets" to control result size

---

### Quick Access

To skip this intro, just ask any product question from the **Scout home screen**. Scout will automatically detect it's a product query and route you here seamlessly.

---

**Data freshness:** Product Searchlight currently uses periodic data extracts rather than live data. Most recent extract: **${DATA_EXTRACT_DATE}**. All functionality is subject to change and improvement. Ideas, questions, and feedback can be provided via the thumbs up or thumbs down buttons below.

**Accuracy Note:** Occasional mistakes are possible — some results may seem strange or unhelpful, and commentary from the AI may not always be accurate. There may also be peculiarities in the underlying data. However, the AI cannot hallucinate the actual data shown on product cards — that information comes directly from the excerpt.

**When in doubt:** Try asking the question again in a different or simpler way. Sometimes being more specific helps, other times being more general works better.
`;

// =============================================================================
// Visual Badge Reference Section
// -----------------------------------------------------------------------------
// Renders the badge-reference subsection of the User Guide using real <Badge>
// components so users see the same pills they see on product cards.
// =============================================================================

const BADGE_GROUP_HEADING_STYLE: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: '#3d4045',
  marginTop: '1.25rem',
  marginBottom: '0.5rem',
};

const REF_TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
  marginBottom: '0.5rem',
};

const REF_TH_STYLE: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  background: '#f8f9fa',
  fontWeight: 600,
  color: '#3d4045',
  border: '1px solid #e3e5e8',
};

const REF_TD_STYLE: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  border: '1px solid #e3e5e8',
  color: '#53575d',
  verticalAlign: 'top',
};

const REF_TD_BADGES_STYLE: React.CSSProperties = {
  ...REF_TD_STYLE,
  whiteSpace: 'normal',
};

const BadgeRowDisplay: React.FC<{ row: BadgeRow }> = ({ row }) => (
  <tr>
    <td style={REF_TD_BADGES_STYLE}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
        {row.examples.map((ex, i) => (
          <Badge key={i} label={ex.label} styleKind={ex.styleKind} icon={ex.icon} />
        ))}
      </div>
    </td>
    <td style={REF_TD_STYLE}>{row.description}</td>
  </tr>
);

const BadgeReferenceTable: React.FC<{ heading: string; rows: BadgeRow[] }> = ({ heading, rows }) => (
  <>
    <h4 style={BADGE_GROUP_HEADING_STYLE}>{heading}</h4>
    <table style={REF_TABLE_STYLE}>
      <thead>
        <tr>
          <th style={{ ...REF_TH_STYLE, width: '40%' }}>Badge</th>
          <th style={REF_TH_STYLE}>Meaning</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => <BadgeRowDisplay key={i} row={row} />)}
      </tbody>
    </table>
  </>
);

const BadgeReferenceSection: React.FC = () => (
  <div className="badge-reference-section">
    <BadgeReferenceTable heading="Safety & Compliance Badges" rows={SAFETY_BADGES} />
    <BadgeReferenceTable heading="Brand & Manufacturing" rows={BRAND_BADGES} />
    <BadgeReferenceTable heading="Industry & Curation" rows={INDUSTRY_BADGES} />
    <BadgeReferenceTable heading="Availability & Configuration" rows={AVAILABILITY_BADGES} />
    <BadgeReferenceTable heading="Search-Mode Indicators" rows={SEARCH_MODE_BADGES} />

    <h4 style={BADGE_GROUP_HEADING_STYLE}>Filter Bar — Facet Categories (above the grid)</h4>
    <table style={REF_TABLE_STYLE}>
      <thead>
        <tr>
          <th style={{ ...REF_TH_STYLE, width: '20%' }}>Facet Type</th>
          <th style={{ ...REF_TH_STYLE, width: '35%' }}>Examples</th>
          <th style={REF_TH_STYLE}>What it filters</th>
        </tr>
      </thead>
      <tbody>
        {FACET_TYPES.map((f, i) => (
          <tr key={i}>
            <td style={REF_TD_STYLE}><strong>{f.type}</strong></td>
            <td style={REF_TD_STYLE}>{f.examples}</td>
            <td style={REF_TD_STYLE}>{f.what}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <h4 style={BADGE_GROUP_HEADING_STYLE}>Product Card — Data Fields</h4>
    <p style={{ color: '#53575d', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
      These appear on the <strong>back</strong> of the flip card or in expanded details.
    </p>
    <table style={REF_TABLE_STYLE}>
      <thead>
        <tr>
          <th style={{ ...REF_TH_STYLE, width: '30%' }}>Field</th>
          <th style={REF_TH_STYLE}>Description</th>
        </tr>
      </thead>
      <tbody>
        {DATA_FIELDS.map((d, i) => (
          <tr key={i}>
            <td style={REF_TD_STYLE}><strong>{d.field}</strong></td>
            <td style={REF_TD_STYLE}>{d.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Full User Guide composition: intro markdown → live badge reference → outro markdown.
const ProductSearchlightUserGuide: React.FC = () => (
  <>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{USER_GUIDE_INTRO_MD}</ReactMarkdown>
    <BadgeReferenceSection />
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{USER_GUIDE_OUTRO_MD}</ReactMarkdown>
  </>
);

/**
 * ImageScaleSettings - A collapsible settings panel for chat preferences.
 * Displays in the top right of the interface.
 * Includes:
 * - Image size slider
 * - User Guide button (only for Product Searchlight)
 * Settings persist across sessions via localStorage (managed by parent hooks).
 */
const ImageScaleSettings: React.FC<ImageScaleSettingsProps> = ({ 
  imageScale,
  onImageScaleChange,
  showUserGuide = false,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);
  
  const handleOpenGuide = () => {
    setShowGuideModal(true);
    setIsExpanded(false); // Close the settings dropdown when opening modal
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onImageScaleChange(parseInt(e.target.value, 10));
  };

  // Compute display labels
  const getScaleLabel = () => {
    if (imageScale === -1) return 'Hidden';
    if (imageScale === 0) return 'Condensed';
    if (imageScale === 100) return 'Full Size';
    if (imageScale <= 33) return 'Small';
    if (imageScale <= 66) return 'Medium';
    return 'Large';
  };

  return (
    <div className={`image-scale-settings ${className}`}>
      {/* Collapsed Button - Settings Cog */}
      <button
        className="image-scale-toggle-btn"
        onClick={toggleExpanded}
        aria-label="Chat settings"
        aria-expanded={isExpanded}
        type="button"
      >
        <Settings className="icon" size={18} />
        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="image-scale-panel">
          {/* Header */}
          <div className="image-scale-header">
            <Settings size={16} />
            <span>Settings</span>
          </div>

          <div className="image-scale-body">
            {/* Image Size Section */}
            <div className="settings-section">
              <div className="settings-section-header">
                <Image size={14} />
                <span>Image Size: {getScaleLabel()}</span>
              </div>

              {/* Slider */}
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={imageScale}
                onChange={handleSliderChange}
                className="image-scale-slider"
                aria-label="Image scale percentage"
                aria-valuenow={imageScale}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuetext={getScaleLabel()}
              />

              {/* Percentage Display */}
              <div className="image-scale-percentage">
                {imageScale}%
              </div>

              {/* Quick Presets */}
              <div className="image-scale-presets">
                <button
                  onClick={() => onImageScaleChange(-1)}
                  className={`preset-btn ${imageScale === -1 ? 'active' : ''}`}
                  type="button"
                  title="Hide all images"
                >
                  Hidden
                </button>
                <button
                  onClick={() => onImageScaleChange(25)}
                  className={`preset-btn ${imageScale === 25 ? 'active' : ''}`}
                  type="button"
                >
                  Small
                </button>
                <button
                  onClick={() => onImageScaleChange(50)}
                  className={`preset-btn ${imageScale === 50 ? 'active' : ''}`}
                  type="button"
                >
                  Medium
                </button>
                <button
                  onClick={() => onImageScaleChange(100)}
                  className={`preset-btn ${imageScale === 100 ? 'active' : ''}`}
                  type="button"
                >
                  Full
                </button>
              </div>

              <p className="image-scale-hint">
                {imageScale === -1 
                  ? 'Images are currently hidden.' 
                  : 'Adjust product image size. Click images to view full size.'}
              </p>
            </div>

            {/* User Guide Section - Only shown for Product Searchlight */}
            {showUserGuide && (
              <>
                <div className="settings-divider" />
                <div className="settings-section">
                  <button
                    onClick={handleOpenGuide}
                    className="user-guide-btn"
                    type="button"
                  >
                    <BookOpen size={14} />
                    <span>User Guide</span>
                  </button>
                  <p className="image-scale-hint">
                    View detailed usage instructions and tips.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* User Guide Modal */}
      {showGuideModal && (
        <div className="user-guide-modal-overlay" onClick={() => setShowGuideModal(false)}>
          <div className="user-guide-modal" onClick={e => e.stopPropagation()}>
            <div className="user-guide-modal-header">
              <div className="user-guide-modal-title">
                <BookOpen size={20} />
                <span>Product Searchlight User Guide</span>
              </div>
              <button 
                className="user-guide-modal-close"
                onClick={() => setShowGuideModal(false)}
                aria-label="Close user guide"
                type="button"
              >
                <X size={20} />
              </button>
            </div>
            <div className="user-guide-modal-content">
              <ProductSearchlightUserGuide />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageScaleSettings;
